import { MiddlewareAPI, AnyAction } from 'redux'
import appActions, { IActionGameSettings } from '../actions/appActions'
import actionTypes from '../actions/actionTypes'
import socketHelper from '../../helpers/connection/socketHelper'
import { playableSessionTypes } from '../../helpers/enums/sessionTypes'
import playerDataSelector from '../selectors/app/playerDataSelector'
import projectorModeSelector from '../selectors/app/projectorModeSelector'
import sessionTypeSelector from '../selectors/app/sessionTypeSelector'
import viewTypes from '../../helpers/enums/views'
import settingsHelper from '../../helpers/utils/settings'
import settingsEnums from '../../helpers/enums/settings'
import { IStore } from '../reducers/index'

import formatParticipants from './helpers/participantsHelper'
import questionActions from '../actions/questionActions'

declare global {
	interface Window {
		gameStates: any
	}
}

window.gameStates = window.gameStates || []

const onAppInitialize = (store: MiddlewareAPI) => {
	const state: IStore = store.getState()
	const sessionType = sessionTypeSelector(state)
	if (state.qs.jwt && playableSessionTypes.includes(sessionType)) {
		socketHelper.joinPlayer(state.qs.jwt, state.qs.gameSessionUid)
	}
}

const onCharacterSelect = (store: MiddlewareAPI, action: AnyAction) => {
	socketHelper.loginStudent(action.id)
}

const onAnswerSubmit = (store: MiddlewareAPI, action: AnyAction) => {
	const playerData = playerDataSelector(store.getState())
	const activityUid = playerData.view.payload.currentActivityUid
	socketHelper.submitStudentAnswer(activityUid, action.selectedAnswersIDs, action.time)
}

const submitSettings = (store: MiddlewareAPI, action: IActionGameSettings) => {
	const projectorMode = projectorModeSelector(store.getState())
	const settings: any = {
		...action.settings,
		[settingsEnums.SETTING_TYPE_HIDE_STUDENT_NAMES]: projectorMode ? 1 : 0,
	}

	// Convert isTimerDisabled to timerMode for backward compatibility
	if (settings.isTimerDisabled !== undefined) {
		settings.timerMode = settings.isTimerDisabled
		delete settings.isTimerDisabled
	}

	socketHelper.submitSettings(settings)
}

const getCurrentNumberOfCorrectAnswersByParticipants = async (store: MiddlewareAPI, _action: AnyAction) => {
	const response = await socketHelper.getCurrentNumberOfCorrectAnswersByParticipants()
	store.dispatch(questionActions.setCurrentNumberOfCorrectAnswersByParticipants(response))
}

const moveToNextActivity = (_store: MiddlewareAPI, _action: AnyAction) => {
	socketHelper.moveToNextActivity()
}

const onPingServer = () => {
	setTimeout(() => {
		onPingServer()
		socketHelper.pingServer()
	}, 1)
}

const calculatePositions = (participants: any) => {
	let minPoints = Number.MAX_SAFE_INTEGER,
		maxPoints = -1

	// First we iterate players to search minimum and maximum player points
	const filteredParticipants = participants.filter((participant: any) => participant.view.type === viewTypes.GAME)
	filteredParticipants.forEach((participant: any) => {
		minPoints = Math.min(minPoints, participant.score)
		maxPoints = Math.max(maxPoints, participant.score)
	})
	filteredParticipants.forEach((participant: any) => {
		participant.view.payload.pos =
			maxPoints === 0 ? 0 : 0.25 + 0.75 * ((participant.score - minPoints) / (maxPoints - minPoints))
	})
	return participants
}

// This method updates old answers format to new answers with media
const formatActivities = (activities: any[]) => {
	return activities.map((activity: any) => {
		return {
			...activity,
			payload: {
				...activity.payload,
				answers: activity.payload.answers.map((answer: any) => {
					if (answer.media) {
						return answer
					}
					return {
						...answer,
						media: {
							type: 'text',
							payload: {
								text: answer.text,
							},
						},
					}
				}),
			},
		}
	})
}

const onGameStart = () => {
	socketHelper.startGame()
}

const socketMiddleware = (store: MiddlewareAPI) => {
	const connectSocket = () => {
		const state = store.getState()
		const sessionType = sessionTypeSelector(state)
		if (playableSessionTypes.includes(sessionType)) {
			const deviceUid = state.app.playerUID
			socketHelper.connect(`${state.app.config.session_api_gateway}/game`, { deviceUid }, () => {
				setTimeout(() => {
					onAppInitialize(store)
				}, 100)
			})

			const updateGameStatus = (data: any) => {
				data.activities = formatActivities(data.activities)

				data.settings = settingsHelper.formatSettings(data.settings)

				if (data.currentView.type === viewTypes.GAME) {
					data.participants = formatParticipants(data, store.getState())
				} else if (data.currentView.type === viewTypes.WINNERS) {
					data.participants = calculatePositions(data.participants)
				}

				store.dispatch(appActions.gameUpdate(data))
			}

			socketHelper.onJoined(() => {
				store.dispatch(appActions.appConnected())
				socketHelper.processPendingEvents()
			})

			socketHelper.onLimitReached(() => {
				store.dispatch(appActions.appLimitReached())
				socketHelper.disconnect()
			})

			socketHelper.onStudentSubmission((studentSubmission) => {
				store.dispatch(appActions.studentSubmissionUpdate(studentSubmission))
			})
			socketHelper.onGameUpdate((gameStatus) => {
				updateGameStatus(gameStatus)
			})
		}
	}

	return (next: (action: unknown) => unknown) => (rawAction: unknown) => {
		const action = rawAction as AnyAction
		next(action)
		switch (action.type) {
			case actionTypes.CHARACTERS_SELECT:
				onCharacterSelect(store, action)
				break
			case actionTypes.APP_CONFIG_LOADED:
				connectSocket()
				break
			case actionTypes.GAME_START:
				onGameStart()
				break
			case actionTypes.QUESTION_ANSWER_SELECT:
				onAnswerSubmit(store, action)
				break
			case actionTypes.APP_PING_SERVER:
				onPingServer()
				break
			case actionTypes.GAME_SETTINGS:
				submitSettings(store, action as IActionGameSettings)
				break
			case actionTypes.GET_CURRENT_NUMBER_OF_CORRECT_ANSWERS_BY_PARTICIPANTS:
				getCurrentNumberOfCorrectAnswersByParticipants(store, action)
				break
			case actionTypes.GAME_MOVE_TO_NEXT_ACTIVITY:
				moveToNextActivity(store, action)
				break
		}
	}
}

export default socketMiddleware
